function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

type Query = string | string[] | undefined;

export function insertPathIDs(path: string, brandID: Query, storeID?: Query) {
  return path
    .replace("[brandID]", brandID as string || 'unreadable')
    .replace("[storeID]", storeID as string || 'unreadable');
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';
const ROOTS_BRAND = (brandID: string) => `/dashboard/${brandID}`;
const ROOTS_STORE = (brandID: string, storeID: string) => `/dashboard/${brandID}/stores/${storeID}`;


// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  register: path(ROOTS_AUTH, '/register'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  verify: path(ROOTS_AUTH, '/verify'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  privacyPolicy: '/privacy-policy',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
  account: '/account',
};

export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  new: path(ROOTS_DASHBOARD, '/new'),
  brand: (brandID: Query) => ({
    root: ROOTS_BRAND(brandID as string),
    stores: path(ROOTS_BRAND(brandID as string), '/stores'),
    users: path(ROOTS_BRAND(brandID as string), '/users'),
    about: path(ROOTS_BRAND(brandID as string), '/about'),
    banking: path(ROOTS_BRAND(brandID as string), '/banking'),
    setup: path(ROOTS_BRAND(brandID as string), '/setup'),
    productTemplates: {
      root: path(`/dashboard/${brandID}`, '/product-templates'),
      new: path(`/dashboard/${brandID}`, '/product-templates/new'),
      edit: (productID: Query) => path(`/dashboard/${brandID}/product-templates/${productID}`, '/edit'),
    },
    store: (storeID: Query) => ({
      root: ROOTS_STORE(brandID as string, storeID as string),
      orders: {
        root: path(ROOTS_STORE(brandID as string, storeID as string), '/orders'),
        today: path(ROOTS_STORE(brandID as string, storeID as string), '/orders/today'),
        tomorrow : path(ROOTS_STORE(brandID as string, storeID as string), '/orders/tomorrow'),
      },
      products: {
        root: path(ROOTS_STORE(brandID as string, storeID as string), '/products'),
        new: path(ROOTS_STORE(brandID as string, storeID as string), '/products/new'),
        view: (productID: Query) => path(ROOTS_STORE(brandID as string, storeID as string), `/products/${productID as string}`),
        edit: (productID: Query) => path(ROOTS_STORE(brandID as string, storeID as string), `/products/${productID as string}/edit`),
      },
      analytics: path(ROOTS_STORE(brandID as string, storeID as string), '/analytics'),
      openingHours: path(ROOTS_STORE(brandID as string, storeID as string), '/opening-hours'),
      about: path(ROOTS_STORE(brandID as string, storeID as string), '/about'),
    }),
  }),

  // Should be unused ------------------------------------------------------
  mail: {
    root: path(ROOTS_DASHBOARD, '/mail'),
    all: path(ROOTS_DASHBOARD, '/mail/all'),
  },
  chat: {
    root: path(ROOTS_DASHBOARD, '/chat'),
    new: path(ROOTS_DASHBOARD, '/chat/new'),
    view: (id: string) => path(ROOTS_DASHBOARD, `/chat/${id}`),
  },
  calendar: path(ROOTS_DASHBOARD, '/calendar'),
  kanban: path(ROOTS_DASHBOARD, '/kanban'),
  permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    new: path(ROOTS_DASHBOARD, '/user/new'),
    list: path(ROOTS_DASHBOARD, '/user/list'),
    cards: path(ROOTS_DASHBOARD, '/user/cards'),
    profile: path(ROOTS_DASHBOARD, '/user/profile'),
    account: path(ROOTS_DASHBOARD, '/user/account'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/user/${id}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, `/user/reece-chung/edit`),
  },
  eCommerce: {
    root: path(ROOTS_DASHBOARD, '/e-commerce'),
    shop: path(ROOTS_DASHBOARD, '/e-commerce/shop'),
    list: path(ROOTS_DASHBOARD, '/e-commerce/list'),
    checkout: path(ROOTS_DASHBOARD, '/e-commerce/checkout'),
    new: path(ROOTS_DASHBOARD, '/e-commerce/product/new'),
    view: (id: string) => path(ROOTS_DASHBOARD, `/e-commerce/product/${id}`),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/e-commerce/product/${id}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-blazer-low-77-vintage/edit'),
    demoView: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-air-force-1-ndestrukt'),
  },
  invoice: {
    root: path(ROOTS_DASHBOARD, '/invoice'),
    list: path(ROOTS_DASHBOARD, '/invoice/list'),
    new: path(ROOTS_DASHBOARD, '/invoice/new'),
    view: (id: string) => path(ROOTS_DASHBOARD, `/invoice/${id}`),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/invoice/${id}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1/edit'),
    demoView: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5'),
  },
  blog: {
    root: path(ROOTS_DASHBOARD, '/blog'),
    posts: path(ROOTS_DASHBOARD, '/blog/posts'),
    new: path(ROOTS_DASHBOARD, '/blog/new'),
    view: (id: string) => path(ROOTS_DASHBOARD, `/blog/post/${id}`),
    demoView: path(ROOTS_DASHBOARD, '/blog/post/apply-these-7-secret-techniques-to-improve-event'),
  },
};

export const PATH_DOCS = 'https://docs-minimals.vercel.app/introduction';
